// Status pills. LIVE is hot-pink with a pulsing dot; other states map to tinted
// hairline pills. Labels are localized via i18n (status.*).
import { useTranslation } from "react-i18next";
import type { StreamStatus } from "../../api/types.js";
import { cn } from "@/lib/utils";

export function LiveBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/15 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-destructive-soft shadow-[0_0_0_1px_hsl(var(--destructive)/0.25)]">
      <span className="h-1.5 w-1.5 animate-livepulse rounded-full bg-destructive" />
      {t("status.live")}
    </span>
  );
}

export function PausedBadge() {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-warn/15 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-warn">
      <span className="h-1.5 w-1.5 rounded-full bg-warn" />
      {t("status.paused")}
    </span>
  );
}

const STYLE: Record<StreamStatus, { cls: string; dot: string }> = {
  live: {
    cls: "bg-destructive/15 text-destructive-soft shadow-[0_0_0_1px_hsl(var(--destructive)/0.25)]",
    dot: "bg-destructive animate-livepulse",
  },
  scheduled: { cls: "bg-warn/15 text-warn", dot: "bg-warn" },
  waiting: { cls: "bg-primary/15 text-primary", dot: "bg-primary" },
  paused: { cls: "bg-warn/15 text-warn", dot: "bg-warn" },
  offline: { cls: "bg-white/[0.06] text-muted-foreground", dot: "bg-muted-foreground" },
  ended: { cls: "bg-white/[0.06] text-muted-foreground", dot: "bg-muted-foreground" },
};

export function StatusBadge({ status }: { status: StreamStatus }) {
  const { t } = useTranslation();
  const p = STYLE[status];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide backdrop-blur-sm",
        p.cls,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", p.dot)} />
      {t(`status.${status}`)}
    </span>
  );
}
